/* eslint-disable @typescript-eslint/naming-convention */
import {
  Component,
  PLATFORM_ID,
  OnInit,
  Renderer2,
  OnDestroy,
  inject,
} from "@angular/core";
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";
import { isPlatformServer, isPlatformBrowser, DOCUMENT } from "@angular/common";
import { SfSiteInfo, SITE_INFO } from "@app/sf-lib/public_api";
import { Router, NavigationEnd, RouterOutlet } from "@angular/router";
import { filter, distinctUntilChanged } from "rxjs/operators";
import { TagManagerService } from "./core/tag-manager/services/tag-manager.service";
import { SeoService } from "./core/seo.service";
import { isNotProduction } from "./business/behavior";
import { trustedOrigin } from "./core/trusted-origin";

@Component({
  selector: "som-root",
  templateUrl: "./app.component.html",
  imports: [RouterOutlet],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly siteInfo = inject<SfSiteInfo>(SITE_INFO);
  private readonly somDocument = inject<Document>(DOCUMENT);
  private readonly matIconRegistry = inject(MatIconRegistry);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly router = inject(Router);
  private readonly tagManager = inject(TagManagerService);
  private readonly renderer = inject(Renderer2);
  private readonly seoService = inject(SeoService);

  public title = "Bonduelle";

  private bingWebmasterTool: HTMLMetaElement;
  private noIndex: HTMLMetaElement;
  private pinterestHu: HTMLMetaElement;
  private pinterestDe: HTMLMetaElement;
  private websiteScriptElement: HTMLScriptElement;
  private organizationScriptElement: HTMLScriptElement;

  public constructor() {
    // Icons are loaded via HTTP — register only in the browser.
    // In SSR, MatIconRegistry would try to fetch SVGs from itself (self-referential HTTP
    // request in the dev server), which fails with status 0. Angular hydration will
    // load the icons correctly on the client side.
    if (isPlatformBrowser(this.platformId)) {
      this.matIconRegistry.addSvgIconSet(
        this.sanitizer.bypassSecurityTrustResourceUrl('/assets/icons/icon-set.svg'),
      );
      this.matIconRegistry.addSvgIcon(
        "som_green_plus",
        this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/green_plus.svg'),
      );
      this.matIconRegistry.addSvgIcon(
        "som_printer",
        this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/printer.svg'),
      );
      this.matIconRegistry.addSvgIcon(
        "som_location",
        this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/location.svg'),
      );
      this.matIconRegistry.addSvgIcon(
        "som_square",
        this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/square.svg'),
      );
      this.matIconRegistry.addSvgIcon(
        "som_puce",
        this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/puce.svg'),
      );
      this.matIconRegistry.addSvgIcon(
        "som_puce_white",
        this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/puce_white.svg'),
      );
      this.matIconRegistry.addSvgIcon(
        "som_phone",
        this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/phone.svg'),
      );
      this.matIconRegistry.addSvgIcon(
        "som_pin",
        this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/pin.svg'),
      );
      this.matIconRegistry.addSvgIcon(
        "som_letter",
        this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/letter.svg'),
      );
      this.matIconRegistry.addSvgIcon(
        "som_upload",
        this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/upload.svg'),
      );
      this.matIconRegistry.addSvgIcon(
        "som_header_phone",
        this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/header/header-phone.svg'),
      );
      this.matIconRegistry.addSvgIcon(
        "som_header_search",
        this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/header/header-wen.svg'),
      );
      this.matIconRegistry.addSvgIcon(
        "som-facebook",
        this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/social/facebook.svg'),
      );
      this.matIconRegistry.addSvgIcon(
        "som-instagram",
        this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/social/instagram.svg'),
      );
      this.matIconRegistry.addSvgIcon(
        "som-youtube",
        this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/social/youtube.svg'),
      );
      this.matIconRegistry.addSvgIcon(
        "som-pinterest",
        this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/social/pinterest.svg'),
      );
      this.matIconRegistry.addSvgIcon(
        "som-tiktok",
        this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/social/tiktok.svg'),
      );
      this.matIconRegistry.addSvgIcon(
        "som-linkedin",
        this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/social/linkedin.svg'),
      );
      this.matIconRegistry.addSvgIcon(
        "som-x",
        this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/social/x.svg'),
      );
      this.matIconRegistry.addSvgIcon(
        "som-facebook-white",
        this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/social/facebook_w.svg'),
      );
      this.matIconRegistry.addSvgIcon(
        "som-instagram-white",
        this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/social/instagram_w.svg'),
      );
      this.matIconRegistry.addSvgIcon(
        "som-youtube-white",
        this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/social/youtube_w.svg'),
      );
      this.matIconRegistry.addSvgIcon(
        "som-pinterest-white",
        this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/social/pinterest_w.svg'),
      );
      this.matIconRegistry.addSvgIcon(
        "som-tiktok-white",
        this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/social/tiktok_w.svg'),
      );
      this.matIconRegistry.addSvgIcon(
        "som-linkedin-white",
        this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/social/linkedin_w.svg'),
      );
      this.matIconRegistry.addSvgIcon(
        "som-x-white",
        this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/social/x_w.svg'),
      );
    }

    this.router.events
      .pipe(
        filter((event) => {
          return event instanceof NavigationEnd;
        }),
        distinctUntilChanged(),
      )
      .subscribe((event: NavigationEnd) => {
        if (
          (event &&
            event.urlAfterRedirects &&
            event.urlAfterRedirects === "/") ||
          (!this.siteInfo.lang.default && event.urlAfterRedirects.length === 3)
        ) {
          // We're on home page. Add microformat
          if (!this.websiteScriptElement) {
            if (isPlatformServer(this.platformId)) {
              const websiteFormat = this.websiteFormat();
              if (websiteFormat) {
                this.websiteScriptElement = this.renderer.createElement("script");
                this.websiteScriptElement.type = `application/ld+json`;
                this.websiteScriptElement.text = JSON.stringify(websiteFormat);
                this.renderer.appendChild(
                  this.somDocument.head,
                  this.websiteScriptElement,
                );
              }
            }
          }
          if (!this.organizationScriptElement) {
            if (isPlatformServer(this.platformId)) {
              const organizationFormat = this.organizationFormat();
              if (organizationFormat) {
                this.organizationScriptElement =
                  this.renderer.createElement("script");
                this.organizationScriptElement.type = `application/ld+json`;
                this.organizationScriptElement.text =
                  JSON.stringify(organizationFormat);
                this.renderer.appendChild(
                  this.somDocument.head,
                  this.organizationScriptElement,
                );
              }
            }
          }
          this.seoService.addHrefLangsLinks();
        } else {
          this.seoService.removeHreflangs();
          // Remove microformat
          if (this.websiteScriptElement && isPlatformServer(this.platformId)) {
            this.renderer.removeChild(
              this.somDocument.head,
              this.websiteScriptElement,
            );
            this.websiteScriptElement = null;
          }
          if (
            this.organizationScriptElement &&
            isPlatformServer(this.platformId)
          ) {
            this.renderer.removeChild(
              this.somDocument.head,
              this.organizationScriptElement,
            );
            this.organizationScriptElement = null;
          }
        }

        if (this.somDocument) {
          if (isPlatformServer(this.platformId)) {
            this.somDocument.documentElement.lang = this.siteInfo.lang.code;
          }
        }
      });

    if (isPlatformBrowser(this.platformId)) {
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.innerHTML = this.tagManager.getGTMScript();
      document.head.appendChild(script);
      const noScript = document.createElement("noscript");
      noScript.innerHTML = this.tagManager.getGTMNoScript();
      document.body.appendChild(noScript);
    }

    if (
      !this.pinterestHu &&
      this.siteInfo &&
      this.siteInfo.lang &&
      this.siteInfo.lang.code === "hu"
    ) {
      this.pinterestHu = this.renderer.createElement("meta");
      this.pinterestHu.name = "p:domain_verify";
      this.pinterestHu.content = "36d961700f36de1ec3e01b816f0dd9ef";
      this.renderer.appendChild(this.somDocument.head, this.pinterestHu);
    }

    if (
      !this.pinterestDe &&
      this.siteInfo &&
      this.siteInfo.lang &&
      this.siteInfo.lang.code === "de"
    ) {
      this.pinterestDe = this.renderer.createElement("meta");
      this.pinterestDe.name = "p:domain_verify";
      this.pinterestDe.content = "5ce1f791464062836c64a51927b013b4";
      this.renderer.appendChild(this.somDocument.head, this.pinterestDe);
    }
  }

  /**
   * JSON-LD `url` and `logo` are ownership claims search engines act upon, exactly
   * like the canonical link in SeoService. On the server siteInfo.origin derives
   * from the incoming Host header, so it is re-checked here — see trusted-origin.ts.
   * Returning null means "emit no block at all": a wrong claim is worse than none.
   */
  private websiteFormat(): object | null {
    const origin = trustedOrigin(this.siteInfo.origin);
    if (!origin) {
      return null;
    }
    return {
      "@context": "https://schema.org/",
      "@type": "WebSite",
      name: "BONDUELLE",
      url: origin,
    };
  }

  private organizationFormat(): object | null {
    const origin = trustedOrigin(this.siteInfo.origin);
    if (!origin) {
      return null;
    }
    return {
      "@context": "https://schema.org/",
      "@type": "Organization",
      url: origin,
      logo: `${origin}/assets/images/logo.png`,
    };
  }

  public ngOnInit(): void {
    //Fix old IOS css issue with height calc
    if (isPlatformBrowser(this.platformId)) {
      this.setVhVariable();
      window.addEventListener("resize", this.setVhVariable);
    }

    const { origin } = this.siteInfo;

    if (!this.noIndex && isNotProduction(origin)) {
      // If we're not in production env then add a noindex script element in head of document
      this.noIndex = this.renderer.createElement("meta");
      this.noIndex.name = "robots";
      this.noIndex.content = "noindex";
      this.renderer.appendChild(this.somDocument.head, this.noIndex);
    }

    if (!this.bingWebmasterTool && !isNotProduction(origin)) {
      this.bingWebmasterTool = this.renderer.createElement("meta");
      this.bingWebmasterTool.name = "msvalidate.01";
      this.bingWebmasterTool.content = "E5E1E5943611F03D1BB492A75F031D07";
      this.renderer.appendChild(this.somDocument.head, this.bingWebmasterTool);
    }
  }

  public setVhVariable(): void {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);
  }

  public ngOnDestroy(): void {
    // Destroy the scripts ... just in case
    if (this.websiteScriptElement) {
      this.renderer.removeChild(
        this.somDocument.head,
        this.websiteScriptElement,
      );
    }
    if (this.organizationScriptElement) {
      this.renderer.removeChild(
        this.somDocument.head,
        this.organizationScriptElement,
      );
    }
    if (this.noIndex) {
      this.renderer.removeChild(this.somDocument.head, this.noIndex);
    }

    if (this.pinterestHu) {
      this.renderer.removeChild(this.somDocument.head, this.pinterestHu);
    }

    if (this.pinterestDe) {
      this.renderer.removeChild(this.somDocument.head, this.pinterestDe);
    }
  }
}
